// PaymentRules.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogContent,
  TextField,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import {
  createRule,
  deleteRule,
  getAllRules,
  updateRule,
} from "../../Endpoints/PaymentRules";
import NotificationModal from "../Generals/Notifications/NotificationModal.js";

import {
  PageRoot,
  Card,
  HeaderCell,
  BodyCell,
  Footer,
  TotalText,
  Toolbar,
} from "./Styles";
import {
  buildTableRowsPerPageOptions,
  TABLE_COMPACT_HEADER_PADDING,
  TABLE_INITIAL_ROWS_PER_PAGE,
} from "../Table/constants.js";

const RULE_TYPE_OPTIONS = [
  { label: "Volume", value: "Volume" },
  { label: "Position profit", value: "PositionProfit" },
  { label: "Symbol", value: "Symbol" },
  { label: "Trade length", value: "TradeLenght" },
  { label: "Balance", value: "Balance" },
  { label: "Equity", value: "Equity" },
  { label: "Login", value: "Login" },
  { label: "Country", value: "Country" },
  { label: "Email", value: "Email" },
  { label: "History profit", value: "HistoryProfit" },
  { label: "Registered ago", value: "resgisteredago" },
  { label: "Total trades today", value: "TotalTradesToday" },
];

const CONDITION_OPTIONS = [
  { label: "=", value: "EQUAL" },
  { label: "+", value: "PLUS" },
  { label: "+=", value: "PLUSEQUAL" },
  { label: "<", value: "LESS" },
  { label: "<=", value: "LESSEQUAL" },
  { label: "!=", value: "DIFFERENT" },
  { label: "Contains", value: "CONTAINS" },
  { label: "Not contains", value: "NOCONTAINS" },
];

const emptyForm = {
  ruleName: "",
  description: "",
  conditions: [
    {
      criteria: "",
      condition: "",
      value: "",
    },
  ],
};

const ROWS_OPTIONS = buildTableRowsPerPageOptions([5, 10, 25, 50, 100]);

const PAYMENT_RULE_COLUMNS = [
  { field: "ruleName", label: "Rule name", minWidth: 180, width: 210 },
  { field: "ruleType", label: "Rule type", minWidth: 150, width: 170 },
  { field: "description", label: "Description", minWidth: 220, width: 260 },
  { field: "condition", label: "Condition", minWidth: 130, width: 140 },
  { field: "value", label: "Value", minWidth: 130, width: 140 },
  {
    field: "actions",
    label: "Actions",
    minWidth: 140,
    width: 150,
    align: "right",
    resizable: false,
  },
];

const initialPaymentRuleColumnWidths = PAYMENT_RULE_COLUMNS.reduce((widths, column) => {
  widths[column.field] = column.width;
  return widths;
}, {});

function normalizePaymentFilters(payload) {
  const paymentFilters = Array.isArray(payload?.payment_filters)
    ? payload.payment_filters
    : [];

  const rules = paymentFilters.map((filterItem) => {
    const firstRule = Array.isArray(filterItem?.payment_rules)
      ? filterItem.payment_rules[0] ?? {}
      : {};

    return {
      id: filterItem?.id ?? "",
      ruleName: filterItem?.name ?? "",
      ruleType: firstRule?.criteria ?? "",
      description: filterItem?.description ?? "",
      condition: firstRule?.condition ?? "",
      value: firstRule?.value ?? "",
      paymentRules: Array.isArray(filterItem?.payment_rules)
        ? filterItem.payment_rules
        : [],
    };
  });

  return {
    email: payload?.email ?? "",
    role: payload?.role ?? "",
    payment_filters: paymentFilters,
    rules,
  };
}

function PaymentRules() {
  const navigate = useNavigate();

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(TABLE_INITIAL_ROWS_PER_PAGE);
  const [columnWidths, setColumnWidths] = useState(initialPaymentRuleColumnWidths);

  // create modal
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deleteRuleTarget, setDeleteRuleTarget] = useState(null);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [successTitle, setSuccessTitle] = useState("Payment rule saved successfully");
  const [successDescription, setSuccessDescription] = useState(
    "The payment rule has been saved successfully and is now available in the system."
  );

  // edit modal
  const [openEdit, setOpenEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // view modal
  const [openView, setOpenView] = useState(false);
  const [viewRuleTarget, setViewRuleTarget] = useState(null);

  // form (reused for create/edit)
  const [form, setForm] = useState(emptyForm);

  const openSuccess = ({ title, description }) => {
    setSuccessTitle(title);
    setSuccessDescription(description);
    setOpenSuccessModal(true);
  };

  const loadRules = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await getAllRules();
      if (!response) {
        setRules([]);
        setError("Failed to load payment rules.");
        return;
      }
      const payload = response?.data?.data ?? response?.data ?? null;
      const normalizedPayload = normalizePaymentFilters(payload);
      const fetchedRules = Array.isArray(normalizedPayload?.rules)
        ? normalizedPayload.rules
        : [];

      setRules(fetchedRules);
    } catch (err) {
      setRules([]);
      setError(err?.message || "Failed to load payment rules.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const filteredRules = useMemo(() => {
    const query = String(search || "").trim().toLowerCase();

    if (!query) return rules;

    return rules.filter((rule) =>
      [
        rule.ruleName,
        rule.ruleType,
        rule.description,
        rule.condition,
        rule.value,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [rules, search]);

  // ===== pagination =====
  const pageCount = useMemo(() => {
    return Math.max(1, Math.ceil(filteredRules.length / rowsPerPage));
  }, [filteredRules.length, rowsPerPage]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRules.slice(start, start + rowsPerPage);
  }, [filteredRules, page, rowsPerPage]);

  const pageItems = useMemo(() => {
    const last = pageCount;
    const current = page + 1;

    if (last <= 4) return Array.from({ length: last }, (_, i) => i + 1);
    if (current <= 2) return [1, 2, 3, 4, "…"];
    if (current >= last - 1) return ["…", last - 3, last - 2, last - 1, last];
    return ["…", current - 1, current, current + 1, "…"];
  }, [page, pageCount]);

  const tableMinWidth = useMemo(() => {
    return PAYMENT_RULE_COLUMNS.reduce(
      (total, column) => total + (columnWidths[column.field] || column.width),
      0
    );
  }, [columnWidths]);

  const columnCellSx = useMemo(() => {
    return PAYMENT_RULE_COLUMNS.reduce((styles, column) => {
      const width = columnWidths[column.field] || column.width;

      styles[column.field] = {
        width,
        minWidth: column.minWidth,
        maxWidth: width,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      };

      return styles;
    }, {});
  }, [columnWidths]);

  const handleColumnResizeStart = useCallback(
    (event, column) => {
      if (column.resizable === false) return;

      event.preventDefault();
      event.stopPropagation();

      const startX = event.clientX;
      const startWidth = columnWidths[column.field] || column.width;
      const previousCursor = document.body.style.cursor;
      const previousUserSelect = document.body.style.userSelect;

      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      const handlePointerMove = (moveEvent) => {
        const nextWidth = Math.max(
          column.minWidth,
          startWidth + moveEvent.clientX - startX
        );

        setColumnWidths((prev) => ({
          ...prev,
          [column.field]: nextWidth,
        }));
      };

      const handlePointerUp = () => {
        document.body.style.cursor = previousCursor;
        document.body.style.userSelect = previousUserSelect;
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointercancel", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
      window.addEventListener("pointercancel", handlePointerUp);
    },
    [columnWidths]
  );

  const handleColumnResizeKeyDown = useCallback((event, column) => {
    if (column.resizable === false) return;

    const direction =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;

    if (!direction) return;

    event.preventDefault();
    event.stopPropagation();

    setColumnWidths((prev) => {
      const currentWidth = prev[column.field] || column.width;

      return {
        ...prev,
        [column.field]: Math.max(column.minWidth, currentWidth + direction * 16),
      };
    });
  }, []);

  // ===== form handlers =====
  const handleChange = (field) => (e) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
  };

  const handleConditionChange = (index, field) => (event) => {
    const nextValue = event.target.value;

    setForm((prev) => ({
      ...prev,
      conditions: prev.conditions.map((conditionItem, conditionIndex) =>
        conditionIndex === index
          ? { ...conditionItem, [field]: nextValue }
          : conditionItem
      ),
    }));
  };

  const handleAddCondition = () => {
    setForm((prev) => ({
      ...prev,
      conditions: [
        ...prev.conditions,
        {
          criteria: "",
          condition: "",
          value: "",
        },
      ],
    }));
  };

  const handleRemoveCondition = (index) => {
    setForm((prev) => {
      if (prev.conditions.length <= 1) return prev;

      return {
        ...prev,
        conditions: prev.conditions.filter((_, conditionIndex) => conditionIndex !== index),
      };
    });
  };

  const getRuleTypeLabel = (value) =>
    RULE_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value ?? "-";

  const getConditionLabel = (value) =>
    CONDITION_OPTIONS.find((option) => option.value === value)?.label ?? value ?? "-";

  const getRuleConditions = (rule) => {
    if (Array.isArray(rule?.paymentRules) && rule.paymentRules.length > 0) {
      return rule.paymentRules.map((ruleItem) => ({
        criteria: ruleItem?.criteria ?? "",
        condition: ruleItem?.condition ?? "",
        value: String(ruleItem?.value ?? ""),
      }));
    }

    if (!rule) return [];

    return [
      {
        criteria: rule.ruleType ?? "",
        condition: rule.condition ?? "",
        value: String(rule.value ?? ""),
      },
    ];
  };

  // ===================== CREATE =====================
  const openCreateModal = () => {
    setForm(emptyForm);
    setCreateError("");
    setOpenCreate(true);
  };
  const closeCreateModal = () => {
    if (creating) return;
    setOpenCreate(false);
    setCreateError("");
  };

  const handleCreate = async () => {
    const payload = {
      name: form.ruleName.trim(),
      description: form.description.trim(),
      payment_rules: form.conditions.map((conditionItem) => ({
        criteria: conditionItem.criteria,
        condition: conditionItem.condition,
        value: String(conditionItem.value ?? "").trim(),
      })),
    };

    try {
      setCreating(true);
      setCreateError("");
      const response = await createRule(payload);
      if (!response) {
        setCreateError("Failed to create payment rule.");
        return;
      }

      await loadRules();
      setOpenCreate(false);
      setForm(emptyForm);
      openSuccess({
        title: "Payment rule created successfully",
        description:
          "The payment rule has been created successfully and is now available in the system.",
      });
    } catch (err) {
      setCreateError(err?.message || "Failed to create payment rule.");
    } finally {
      setCreating(false);
    }
  };

  // ===================== EDIT =====================
  const openEditModal = (row) => {
    const rowConditions = getRuleConditions(row);

    setEditingId(row.id);
    setSaveError("");
    setForm({
      ruleName: row.ruleName ?? "",
      description: row.description ?? "",
      conditions: rowConditions,
    });
    setOpenEdit(true);
  };

  const closeEditModal = () => {
    if (saving) return;
    setOpenEdit(false);
    setEditingId(null);
    setSaveError("");
  };

  const handleSave = async () => {
    const currentRule = rules.find((rule) => rule.id === editingId);
    const payload = {
      id: editingId,
      name: form.ruleName.trim(),
      description: form.description.trim(),
      payment_rules: form.conditions.map((conditionItem, index) => {
        const currentRuleItem = currentRule?.paymentRules?.[index] ?? {};

        return {
          ...(currentRuleItem?.id ? { id: currentRuleItem.id } : {}),
          criteria: conditionItem.criteria,
          condition: conditionItem.condition,
          value: String(conditionItem.value ?? "").trim(),
        };
      }),
    };

    try {
      setSaving(true);
      setSaveError("");
      const response = await updateRule(payload);
      if (!response) {
        setSaveError("Failed to update payment rule.");
        return;
      }

      await loadRules();
      setOpenEdit(false);
      setEditingId(null);
      openSuccess({
        title: "Payment rule updated successfully",
        description:
          "The payment rule has been updated successfully and the changes are now available in the system.",
      });
    } catch (err) {
      setSaveError(err?.message || "Failed to update payment rule.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (row) => {
    setDeleteRuleTarget(row);
    setOpenDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    if (deletingId) return;
    setOpenDeleteConfirm(false);
    setDeleteRuleTarget(null);
  };

  const handleConfirmDelete = async () => {
    const id = deleteRuleTarget?.id;
    if (!id) {
      setError("Invalid payment rule id.");
      return;
    }

    try {
      setDeletingId(id);
      setError("");
      const response = await deleteRule(id);
      if (!response) {
        setError("Failed to delete payment rule.");
        return;
      }

      await loadRules();
      setOpenDeleteConfirm(false);
      setDeleteRuleTarget(null);
      openSuccess({
        title: "Payment rule deleted successfully",
        description:
          "The payment rule has been deleted successfully and is no longer available in the system.",
      });
    } catch (err) {
      setError(err?.message || "Failed to delete payment rule.");
    } finally {
      setDeletingId(null);
    }
  };

  const openViewModal = (row) => {
    setViewRuleTarget(row);
    setOpenView(true);
  };

  const closeViewModal = () => {
    setOpenView(false);
    setViewRuleTarget(null);
  };

  useEffect(() => {
    setPage(0);
  }, [search]);

  useEffect(() => {
    if (page > pageCount - 1) {
      setPage(pageCount - 1);
    }
  }, [page, pageCount]);

  useEffect(() => {
    setPage(0);
  }, [rowsPerPage]);

  const arrowBtnSx = (disabled) => ({
    minWidth: 30,
    height: 30,
    borderRadius: "10px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E5E7EB",
    color: disabled ? "#9CA3AF" : "#374151",
    boxShadow: "none",
    padding: 0,
    "&:hover": {
      backgroundColor: "#F9FAFB",
      borderColor: "#D1D5DB",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      backgroundColor: "#FFFFFF",
      borderColor: "#E5E7EB",
      color: "#9CA3AF",
      opacity: 1,
    },
  });

  // ===== shared validation =====
  const isFormValid =
    !!form.ruleName.trim() &&
    form.conditions.length > 0 &&
    form.conditions.every(
      (conditionItem) =>
        !!conditionItem.criteria &&
        !!conditionItem.condition &&
        !!String(conditionItem.value ?? "").trim()
    );

  const renderPaymentRuleForm = ({
    title,
    errorMessage,
    onClose,
    onSubmit,
    submitting,
    submitLabel,
    submittingLabel,
  }) => (
    <DialogContent sx={{ p: 0 }}>
      <Box
        sx={{
          px: 3,
          py: 2.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          borderBottom: "1px solid #EEF0F4",
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 900, color: "#111827" }}>
          {title}
        </Typography>

        <IconButton onClick={onClose} size="small" disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

        <Box>
          <Typography sx={{ mb: 1.5, fontSize: 16, fontWeight: 900, color: "#111827" }}>
            General Information
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box>
              <Typography sx={{ mb: 0.5, fontSize: 12, fontWeight: 700, color: "#6B7280" }}>
                Rule Name <Box component="span" sx={{ color: "#EB0000" }}>*</Box>
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter rule name"
                value={form.ruleName}
                onChange={handleChange("ruleName")}
                disabled={submitting}
              />
            </Box>

            <Box>
              <Typography sx={{ mb: 0.5, fontSize: 12, fontWeight: 700, color: "#6B7280" }}>
                Description
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter description(optional)"
                value={form.description}
                onChange={handleChange("description")}
                disabled={submitting}
              />
            </Box>
          </Box>
        </Box>

        <Box>
          <Typography sx={{ mb: 0.5, fontSize: 16, fontWeight: 900, color: "#111827" }}>
            Conditions
          </Typography>
          <Typography sx={{ mb: 1.5, fontSize: 12, color: "#6B7280" }}>
            All conditions must be met for the rule to apply
          </Typography>

          <Box
            sx={{
              border: "1px solid #CBD5E1",
              borderRadius: 2,
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {form.conditions.map((conditionItem, index) => (
              <Box
                key={`condition-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1.25fr 1.15fr 1fr auto",
                  },
                  gap: 1.5,
                  alignItems: "flex-end",
                }}
              >
                <Box>
                  <Typography sx={{ mb: 0.5, fontSize: 12, fontWeight: 700, color: "#6B7280" }}>
                    Criteria
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      displayEmpty
                      value={conditionItem.criteria}
                      onChange={handleConditionChange(index, "criteria")}
                      disabled={submitting}
                    >
                      <MenuItem value="">
                        <em>Select criteria</em>
                      </MenuItem>
                      {RULE_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography sx={{ mb: 0.5, fontSize: 12, fontWeight: 700, color: "#6B7280" }}>
                    Condition
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      displayEmpty
                      value={conditionItem.condition}
                      onChange={handleConditionChange(index, "condition")}
                      disabled={submitting}
                    >
                      <MenuItem value="">
                        <em>Select condition</em>
                      </MenuItem>
                      {CONDITION_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box>
                  <Typography sx={{ mb: 0.5, fontSize: 12, fontWeight: 700, color: "#6B7280" }}>
                    Value
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter value"
                    value={conditionItem.value}
                    onChange={handleConditionChange(index, "value")}
                    disabled={submitting}
                  />
                </Box>

                <IconButton
                  onClick={() => handleRemoveCondition(index)}
                  disabled={submitting || form.conditions.length <= 1}
                  sx={{
                    width: 40,
                    height: 40,
                    border: "1px solid #FECACA",
                    borderRadius: 2,
                    color: "#EB0000",
                    alignSelf: { xs: "flex-start", sm: "flex-end" },
                    "&.Mui-disabled": {
                      color: "#FCA5A5",
                      borderColor: "#FEE2E2",
                    },
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddCondition}
            disabled={submitting}
            sx={{
              mt: 1.5,
              textTransform: "none",
              fontWeight: 900,
              borderRadius: 2,
              borderColor: "#CBD5E1",
              color: "#111827",
              "&:hover": {
                borderColor: "#94A3B8",
                backgroundColor: "#F8FAFC",
              },
            }}
          >
            Add Condition
          </Button>
        </Box>

        <Box
          sx={{
            pt: 5,
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              minWidth: 110,
              textTransform: "none",
              borderRadius: 2,
              color: "#374151",
              borderColor: "#CBD5E1",
            }}
            disabled={submitting}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={onSubmit}
            sx={{ minWidth: 116, textTransform: "none", fontWeight: 900, borderRadius: 2 }}
            disabled={!isFormValid || submitting}
          >
            {submitting ? submittingLabel : submitLabel}
          </Button>
        </Box>
      </Box>
    </DialogContent>
  );

  const viewConditions = getRuleConditions(viewRuleTarget);

  return (
    <PageRoot>
      <Box
        sx={{
          width: "calc(100% - 10px)",
          maxWidth: "calc(100% - 10px)",
          px: 4,
          pt: 2,
          pb: 4,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Button
          startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate(-1)}
          sx={{ textTransform: "none", mb: 1.5, color: "#111827", alignSelf: "flex-start" }}
        >
          Go back
        </Button>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "nowrap",
            mb: 3,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5 }}>
              Payment rules
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Edit existing rules or define new ones to apply across every account.
            </Typography>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              ml: "auto",
              flexShrink: 0,
              whiteSpace: "nowrap",
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 800,
              backgroundColor: "#EB0000",
              "&:hover": {
                backgroundColor: "#D50000",
              },
            }}
            onClick={openCreateModal}
          >
            Create new rule
          </Button>
        </Box>

        <Card>
          <Toolbar sx={{ justifyContent: "space-between" }}>
            <TextField
              size="small"
              placeholder="Search rules"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: { xs: "100%", sm: 340 },
                backgroundColor: "#FFFFFF",
                borderRadius: "10px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#6B7280" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Toolbar>

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <TableContainer sx={{ backgroundColor: "#FFFFFF", borderRadius: 3, overflowX: "auto" }}>
            <Table
              sx={{
                backgroundColor: "#FFFFFF",
                minWidth: tableMinWidth,
                tableLayout: "fixed",
              }}
            >
              <colgroup>
                {PAYMENT_RULE_COLUMNS.map((column) => (
                  <col
                    key={column.field}
                    style={{
                      width: `${columnWidths[column.field] || column.width}px`,
                    }}
                  />
                ))}
              </colgroup>
              <TableHead>
                <TableRow>
                  {PAYMENT_RULE_COLUMNS.map((column) => (
                    <HeaderCell
                      key={column.field}
                      align={column.align || "left"}
                      sx={{
                        ...columnCellSx[column.field],
                        position: "relative",
                        pr:
                          column.resizable === false
                            ? 2
                            : TABLE_COMPACT_HEADER_PADDING,
                        userSelect: "none",
                      }}
                    >
                      <Box
                        component="span"
                        title={column.label}
                        sx={{
                          display: "block",
                          lineHeight: "18px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {column.label}
                      </Box>
                      {column.resizable !== false && (
                        <Box
                          component="span"
                          role="separator"
                          aria-label={`Resize ${column.label} column`}
                          aria-orientation="vertical"
                          aria-valuemin={column.minWidth}
                          aria-valuenow={columnWidths[column.field] || column.width}
                          tabIndex={0}
                          onPointerDown={(event) =>
                            handleColumnResizeStart(event, column)
                          }
                          onKeyDown={(event) =>
                            handleColumnResizeKeyDown(event, column)
                          }
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: "12px",
                            height: "100%",
                            cursor: "col-resize",
                            touchAction: "none",
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              top: "22%",
                              bottom: "22%",
                              left: "50%",
                              width: "2px",
                              transform: "translateX(-50%)",
                              borderRadius: "2px",
                              backgroundColor: "rgba(21, 32, 42, 0.22)",
                            },
                            "&:hover::after, &:focus-visible::after": {
                              backgroundColor: "#15202A",
                            },
                            "&:focus-visible": {
                              outline: "2px solid #15202A",
                              outlineOffset: "-2px",
                            },
                          }}
                        />
                      )}
                    </HeaderCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <BodyCell colSpan={6} sx={{ py: 6 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1.5,
                        }}
                      >
                        <CircularProgress size={24} />
                        <Typography sx={{ fontWeight: 700, color: "#111827" }}>
                          Loading rules...
                        </Typography>
                      </Box>
                    </BodyCell>
                  </TableRow>
                ) : pagedRows.length > 0 ? (
                  pagedRows.map((r) => (
                    <TableRow key={r.id} hover>
                      <BodyCell title={r.ruleName} sx={columnCellSx.ruleName}>
                        {r.ruleName}
                      </BodyCell>
                      <BodyCell
                        title={
                          RULE_TYPE_OPTIONS.find((option) => option.value === r.ruleType)?.label ??
                          r.ruleType
                        }
                        sx={columnCellSx.ruleType}
                      >
                        {RULE_TYPE_OPTIONS.find((option) => option.value === r.ruleType)?.label ??
                          r.ruleType}
                      </BodyCell>
                      <BodyCell title={r.description} sx={columnCellSx.description}>
                        {r.description}
                      </BodyCell>
                      <BodyCell
                        title={
                          CONDITION_OPTIONS.find((option) => option.value === r.condition)?.label ??
                          r.condition
                        }
                        sx={columnCellSx.condition}
                      >
                        {CONDITION_OPTIONS.find((option) => option.value === r.condition)?.label ??
                          r.condition}
                      </BodyCell>
                      <BodyCell title={r.value} sx={columnCellSx.value}>
                        {r.value}
                      </BodyCell>

                      <BodyCell align="right" sx={{ ...columnCellSx.actions, pr: "10px" }}>
                        <Stack direction="row" justifyContent="flex-end" spacing={1}>
                          <IconButton size="small" onClick={() => openViewModal(r)}>
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>

                          <IconButton size="small" onClick={() => openEditModal(r)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>

                          <IconButton
                            size="small"
                            onClick={() => handleDelete(r)}
                            disabled={deletingId === r.id}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </BodyCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <BodyCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                      No payment rules found.
                    </BodyCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Footer>
            <TotalText>Total: {filteredRules.length}</TotalText>
            <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
              Pages:&nbsp; <b style={{ color: "#111827" }}>{pageCount}</b>
            </Typography>

            <Typography sx={{ fontSize: 13, color: "#6B7280" }}>Rows:</Typography>

            <Select
              size="small"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              sx={{ height: 32, minWidth: 70, borderRadius: 2, ".MuiSelect-select": { py: 0.5 } }}
              MenuProps={{ PaperProps: { sx: { borderRadius: 2, mt: 0.5 } } }}
            >
              {ROWS_OPTIONS.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Button
                size="small"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                sx={arrowBtnSx(page === 0)}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
              </Button>

              {pageItems.map((it, idx) => {
                if (it === "…") {
                  return (
                    <Typography key={`dots-${idx}`} sx={{ fontSize: 14, color: "#9CA3AF", px: 0.5 }}>
                      …
                    </Typography>
                  );
                }

                const pageNumber = it;
                const isActive = pageNumber === page + 1;

                return (
                  <Button
                    key={`p-${pageNumber}`}
                    size="small"
                    onClick={() => setPage(pageNumber - 1)}
                    sx={{
                      minWidth: 28,
                      height: 28,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: isActive ? 900 : 700,
                      color: isActive ? "#111827" : "#6B7280",
                      backgroundColor: isActive ? "#EEF2FF" : "transparent",
                      "&:hover": { backgroundColor: isActive ? "#EEF2FF" : "#F3F4F6" },
                    }}
                  >
                    {pageNumber}
                  </Button>
                );
              })}

              <Button
                size="small"
                disabled={page >= pageCount - 1}
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                sx={arrowBtnSx(page >= pageCount - 1)}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 16 }} />
              </Button>
            </Box>
          </Footer>
        </Card>
      </Box>

      {/* ===================== MODAL: CREATE RULE ===================== */}
      <Dialog
        open={openCreate}
        onClose={closeCreateModal}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 4, maxWidth: 760 } }}
      >
        {renderPaymentRuleForm({
          title: "New Payment Rule",
          errorMessage: createError,
          onClose: closeCreateModal,
          onSubmit: handleCreate,
          submitting: creating,
          submitLabel: "Save Rule",
          submittingLabel: "Saving...",
        })}
      </Dialog>

      {/* ===================== MODAL: EDIT RULE ===================== */}
      <Dialog
        open={openEdit}
        onClose={closeEditModal}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 4, maxWidth: 760 } }}
      >
        {renderPaymentRuleForm({
          title: "Edit Payment Rule",
          errorMessage: saveError,
          onClose: closeEditModal,
          onSubmit: handleSave,
          submitting: saving,
          submitLabel: "Update Rule",
          submittingLabel: "Updating...",
        })}
      </Dialog>

      <Dialog
        open={openView}
        onClose={closeViewModal}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 4, maxWidth: 760 } }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              px: 3,
              py: 2.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              borderBottom: "1px solid #EEF0F4",
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: "#111827" }}>
              View Payment Rule
            </Typography>

            <IconButton onClick={closeViewModal} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, minHeight: 520, display: "flex", flexDirection: "column" }}>
            <Typography sx={{ mb: 5, fontSize: 18, fontWeight: 900, color: "#111827" }}>
              {viewRuleTarget?.ruleName || "-"}
            </Typography>

            <Typography sx={{ mb: 1, fontSize: 16, fontWeight: 900, color: "#111827" }}>
              Description
            </Typography>
            <Typography sx={{ mb: 3, fontSize: 14, color: "#6B7280" }}>
              {viewRuleTarget?.description || "-"}
            </Typography>

            <Typography sx={{ fontSize: 16, fontWeight: 900, color: "#111827" }}>
              Conditions({viewConditions.length})
            </Typography>
            <Typography sx={{ mb: 2, fontSize: 12, color: "#6B7280" }}>
              All conditions must be met for the rule to apply
            </Typography>

            <Box
              sx={{
                border: "1px solid #CBD5E1",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  borderBottom: "1px solid #EEF0F4",
                  backgroundColor: "#FFFFFF",
                }}
              >
                {["Criteria", "Condition", "Value"].map((header) => (
                  <Typography
                    key={header}
                    sx={{
                      px: 2,
                      py: 1.5,
                      fontSize: 12,
                      fontWeight: 900,
                      color: "#111827",
                    }}
                  >
                    {header}
                  </Typography>
                ))}
              </Box>

              {viewConditions.length > 0 ? (
                viewConditions.map((conditionItem, index) => (
                  <Box
                    key={`view-condition-${index}`}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      borderBottom:
                        index === viewConditions.length - 1
                          ? "none"
                          : "1px solid #EEF0F4",
                    }}
                  >
                    <Typography sx={{ px: 2, py: 1.75, fontSize: 14, color: "#6B7280" }}>
                      {getRuleTypeLabel(conditionItem.criteria)}
                    </Typography>
                    <Typography sx={{ px: 2, py: 1.75, fontSize: 14, color: "#6B7280" }}>
                      {getConditionLabel(conditionItem.condition)}
                    </Typography>
                    <Typography sx={{ px: 2, py: 1.75, fontSize: 14, color: "#6B7280" }}>
                      {conditionItem.value || "-"}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography sx={{ px: 2, py: 2, fontSize: 14, color: "#6B7280" }}>
                  No conditions found.
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: "auto", pt: 6, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={closeViewModal}
                sx={{
                  minWidth: 110,
                  textTransform: "none",
                  borderRadius: 2,
                  color: "#374151",
                  borderColor: "#CBD5E1",
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <NotificationModal
        open={openDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        iconType="warning"
        title="Delete payment rule"
        message={
          <>
            Are you sure you want to delete{" "}
            <strong>{deleteRuleTarget?.ruleName || "this payment rule"}</strong>? This
            action cannot be undone.
          </>
        }
        onRefuseLabel="Cancel"
        onAcceptLabel={deletingId ? "Deleting..." : "Delete"}
        onRefuse={handleCloseDeleteConfirm}
        onAccept={handleConfirmDelete}
      />

      <Dialog
        open={openSuccessModal}
        onClose={() => setOpenSuccessModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            minWidth: { xs: "calc(100% - 32px)", sm: 460 },
          },
        }}
      >
        <DialogContent sx={{ p: 3, textAlign: "center" }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
            <CheckCircleIcon sx={{ fontSize: 54, color: "#16A34A" }} />
          </Box>

          <Typography sx={{ fontSize: 22, fontWeight: 900, color: "#111827" }}>
            {successTitle}
          </Typography>

          <Typography sx={{ mt: 1, fontSize: 14, color: "#6B7280" }}>
            {successDescription}
          </Typography>

          <Box sx={{ mt: 3, display: "flex", gap: 1.2, justifyContent: "center" }}>
            <Button
              variant="contained"
              color="error"
              onClick={() => setOpenSuccessModal(false)}
              sx={{
                textTransform: "none",
                fontWeight: 900,
                borderRadius: 2,
              }}
            >
              Continue
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </PageRoot>
  );
}

export default PaymentRules;
